/* eslint-disable no-console */
import { createContext, useContext, useEffect, useState, startTransition } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Custom hook for theme persistence and system preference detection
export function useThemeLogic(): ThemeContextType {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Since we initialize theme in HTML, we can just read the current state
    if (typeof window !== 'undefined') {
      // Check if dark class is already applied by the initialization script
      if (document.documentElement.classList.contains('dark')) {
        return 'dark';
      }

      // Also check localStorage as a fallback
      try {
        const stored = localStorage.getItem('theme') as Theme | null;
        if (stored === 'light' || stored === 'dark') {
          return stored;
        }
      } catch (error) {
        console.warn('localStorage not available:', error);
      }
    }

    return 'light';
  });

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    // Defer DOM operations to prevent blocking the main thread
    window.requestAnimationFrame(() => {
      const root = document.documentElement;

      // Use CSS transitions to smooth theme changes
      root.style.transition = 'background-color 0.2s ease, color 0.2s ease';

      if (theme === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }

      // Clean up transition after animation
      setTimeout(() => {
        root.style.transition = '';
      }, 200);
    });

    // Save to localStorage (non-blocking)
    try {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(
          () => {
            localStorage.setItem('theme', theme);
          },
          { timeout: 100 }
        );
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          localStorage.setItem('theme', theme);
        }, 0);
      }
    } catch (error) {
      // localStorage might not be available
      console.warn('Could not save theme to localStorage:', error);
    }
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = (e: MediaQueryListEvent) => {
        try {
          // Only auto-switch if user hasn't manually set a preference
          const stored = localStorage.getItem('theme');
          if (!stored) {
            // Use startTransition for non-urgent state updates
            startTransition(() => {
              setThemeState(e.matches ? 'dark' : 'light');
            });
          }
        } catch (error) {
          console.warn('Could not access localStorage in theme change handler:', error);
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } catch (error) {
      console.warn('Could not set up system theme change listener:', error);
    }
  }, []);

  const toggleTheme = () => {
    // Use startTransition for user-initiated theme changes
    startTransition(() => {
      setThemeState(current => (current === 'light' ? 'dark' : 'light'));
    });
  };

  const setTheme = (newTheme: Theme) => {
    // Use startTransition for programmatic theme changes
    startTransition(() => {
      setThemeState(newTheme);
    });
  };

  return { theme, toggleTheme, setTheme };
}

export { ThemeContext };
export type { Theme };
